// Legal pages translations (Terms, Copyright, Tariff) — 5 languages.
// Kept in a separate file to keep translations.js lean.

export const LEGAL = {
  en: {
    terms: {
      eyebrow: "— Legal",
      title: "Terms & Conditions",
      sections: {
        s1_title: "1. Pre-payment",
        s1_body: "All SwissAlpenLine reservations are confirmed upon full advance payment, similar to airline bookings. Funds are held by our payment provider and only released to the company after the trip is completed. This protects both parties: the client is guaranteed the service, and the company is protected against unfounded chargebacks.",
        s2_title: "2. Service hours & surcharges",
        table_tier: "Tier",
        table_hours: "Hours",
        table_surcharge: "Surcharge",
        tier_normal: "Normal",
        tier_intermediate: "Intermediate",
        tier_night: "Night",
        s3_title: "3. Cancellation policy",
        table_notice: "Notice",
        table_outcome: "Outcome",
        notice_48: "≥ 48 h before",
        notice_24_48: "24 – 48 h before",
        notice_lt24: "< 24 h or no-show",
        outcome_full_refund: "Full refund",
        outcome_half: "50% refund + 50% credit (6 mo.)",
        outcome_full_credit: "Full credit (6 months)",
        s3_note: "No reservation is ever forfeited — the amount paid is either refunded or converted into a credit valid for six months on any future trip.",
        s4_title: "4. Reservation changes",
        s4_body: "Free of charge until 48 h before pickup, subject to availability. Changes within less than 48 h follow the cancellation table above.",
        s5_title: "5. Off-peak notice",
        s5_body: "Intermediate hours: minimum 6 h notice. Night hours: minimum 12 h notice.",
        s6_title: "6. Delays & waiting time",
        s6_body: "Airports: 45 minutes of free waiting time after landing. Other pickups: 10 minutes. Beyond that, the standard waiting rate applies.",
        s7_title: "7. Fraud protection",
        s7_list: [
          "Identity verification is required for reservations above CHF 500 — cardholder / Twint name must match the passenger.",
          "SwissAlpenLine reserves the right to refuse service on reasonable suspicion of fraud, with immediate full refund.",
          "Bad-faith chargebacks after a delivered service may constitute fraud under Art. 146 of the Swiss Criminal Code and will be documented and reported to the competent authorities.",
          "Automatic record of proof of service (pickup and drop-off timestamps) is kept for chargeback defence.",
        ],
        s8_title: "8. Legal basis",
        s8_body: "This policy relies on the freedom of contract in Art. 1 & 19–20 of the Swiss Code of Obligations (CO), the limited-capacity reservation logic (Art. 377 CO), the transparency requirements of the Federal Act against Unfair Competition (Art. 8 UCA), and the Price Indication Ordinance (PIO), which requires that the final price — including surcharges — be shown before payment confirmation.",
      },
    },
    copyright: {
      eyebrow: "— Legal",
      title: "Copyright & Intellectual Property",
      lead: "© SwissAlpenLine. All rights reserved.",
      body1: "All content on this site — text, photographs, visual identity, logo and design — is protected by the Swiss Federal Act on Copyright and Related Rights (CopA, SR 231.1). Reproduction, distribution or use of this content, in whole or in part, without the prior written authorisation of SwissAlpenLine is prohibited.",
      body2: "The SwissAlpenLine logo and visual identity are the exclusive property of the company and are not licensed for use by third parties.",
      contact: "For licensing enquiries, please contact info@swissalpenline.ch.",
    },
    tariff: {
      eyebrow: "— Tariff",
      title: "Tariffs",
      subtitle: "Final prices, no hidden costs — as required by the Swiss Price Indication Ordinance.",
      block_transfer: "Transfer — point to point",
      block_fullday: "Chauffeur for a day",
      block_courier: "Courier — Documents",
      block_hours: "Hourly surcharges",
      row_base: "Base fare",
      row_km_short: "Per km (up to 80 km)",
      row_km_long: "Per km (beyond 80 km)",
      row_child_seat: "Child seat",
      row_pet_no: "Pet, no carrier",
      row_pet_yes: "Pet + carrier provided",
      row_full_base: "Base — 8 h",
      row_full_extra: "Extra hour",
      row_full_region: "Region",
      row_region_val: "CH · FR · DE · IT",
      row_courier_base: "Base",
      row_courier_km: "Per km",
      row_courier_night: "Night (22:00 – 06:00)",
      row_courier_weekend: "Weekend",
      row_normal: "Normal 08:00 – 19:00",
      row_intermediate: "Intermediate 06:00 – 08:00 · 19:00 – 22:00",
      row_night: "Night 22:00 – 06:00",
      row_weekend: "Weekend (Sat & Sun)",
      footer_note: "Prices in Swiss Francs (CHF). On-board payment also accepted in EUR / USD with a CHF-favoured conversion.",
      footer_see: "See",
      footer_terms: "Terms & Conditions",
      footer_for: "for the full policy.",
    },
  },

  pt: {
    terms: {
      eyebrow: "— Legal",
      title: "Termos e Condições",
      sections: {
        s1_title: "1. Pré-pagamento",
        s1_body: "Todas as reservas da SwissAlpenLine são confirmadas mediante pagamento integral e antecipado, à semelhança das companhias aéreas. Os fundos são retidos pelo nosso processador de pagamentos e apenas libertados à empresa após a conclusão da viagem. Esta arquitetura protege ambas as partes: o cliente tem a garantia do serviço, e a empresa fica protegida contra chargebacks infundados.",
        s2_title: "2. Horários e sobretaxas",
        table_tier: "Faixa",
        table_hours: "Horário",
        table_surcharge: "Sobretaxa",
        tier_normal: "Normal",
        tier_intermediate: "Intermédio",
        tier_night: "Noturno",
        s3_title: "3. Política de cancelamento",
        table_notice: "Antecedência",
        table_outcome: "Condição",
        notice_48: "48 h ou mais antes",
        notice_24_48: "Entre 24 e 48 h antes",
        notice_lt24: "Menos de 24 h ou não comparência",
        outcome_full_refund: "Reembolso total",
        outcome_half: "50% reembolsado + 50% crédito (6 meses)",
        outcome_full_credit: "Crédito total (6 meses)",
        s3_note: "Nenhuma reserva é simplesmente perdida — o valor pago é sempre reembolsado ou convertido em crédito válido por seis meses para qualquer viagem futura.",
        s4_title: "4. Alterações de reserva",
        s4_body: "Gratuitas até 48 h antes da recolha, sujeitas a disponibilidade. Alterações com menos de 48 h seguem a tabela de cancelamento acima.",
        s5_title: "5. Reservas fora do horário normal",
        s5_body: "Horário intermédio: aviso mínimo de 6 horas. Horário noturno: aviso mínimo de 12 horas.",
        s6_title: "6. Atrasos e tempo de espera",
        s6_body: "Aeroportos: 45 minutos de espera gratuita após aterragem. Outras recolhas: 10 minutos. Após esse período, aplica-se a tarifa de espera em vigor.",
        s7_title: "7. Proteção contra fraude",
        s7_list: [
          "Verificação de identidade obrigatória para reservas acima de CHF 500 — o nome no cartão/Twint deve corresponder ao nome do passageiro.",
          "A SwissAlpenLine reserva-se o direito de recusa de serviço em caso de suspeita fundamentada de fraude, com reembolso integral imediato.",
          "Contestações de pagamento (chargebacks) de má-fé após a prestação do serviço podem constituir fraude nos termos do Art. 146.º do Código Penal suíço, e serão documentadas e reportadas às autoridades competentes.",
          "Registo automático de prova de prestação do serviço (hora de recolha e entrega, confirmada no sistema) para contestar eventuais chargebacks indevidos.",
        ],
        s8_title: "8. Base legal",
        s8_body: "Esta política assenta na liberdade contratual prevista nos artigos 1.º e 19.º–20.º do Código das Obrigações (CO) suíço, na lógica de reserva de capacidade limitada (Art. 377.º CO), e cumpre os requisitos de transparência da Lei contra a Concorrência Desleal (Art. 8.º LCD) e da Ordenança sobre a Indicação dos Preços (OIP), que exige que o preço final — incluindo sobretaxas — seja sempre mostrado antes da confirmação do pagamento.",
      },
    },
    copyright: {
      eyebrow: "— Legal",
      title: "Direitos de Autor e Propriedade Intelectual",
      lead: "© SwissAlpenLine. Todos os direitos reservados.",
      body1: "Todo o conteúdo deste site — textos, fotografias, identidade visual, logótipo e design — está protegido pela Lei Federal suíça sobre o Direito de Autor e Direitos Conexos (LDA, RS 231.1). É proibida a reprodução, distribuição ou utilização total ou parcial deste conteúdo sem autorização escrita prévia da SwissAlpenLine.",
      body2: "O logótipo e identidade visual da SwissAlpenLine são propriedade exclusiva da empresa e não estão licenciados para uso por terceiros.",
      contact: "Para pedidos de licenciamento, contacte info@swissalpenline.ch.",
    },
    tariff: {
      eyebrow: "— Tarifário",
      title: "Tarifário",
      subtitle: "Preços finais, sem custos escondidos — conforme exigido pela Ordenança suíça sobre a Indicação dos Preços.",
      block_transfer: "Transfer — ponto a ponto",
      block_fullday: "Chauffeur por um dia",
      block_courier: "Courier — Documentos",
      block_hours: "Sobretaxas horárias",
      row_base: "Tarifa base",
      row_km_short: "Por km (até 80 km)",
      row_km_long: "Por km (acima de 80 km)",
      row_child_seat: "Cadeira de criança",
      row_pet_no: "Animal, sem transportadora",
      row_pet_yes: "Animal + transportadora fornecida",
      row_full_base: "Base — 8 h",
      row_full_extra: "Hora extra",
      row_full_region: "Região",
      row_region_val: "CH · FR · DE · IT",
      row_courier_base: "Base",
      row_courier_km: "Por km",
      row_courier_night: "Noturno (22h00 – 06h00)",
      row_courier_weekend: "Fim-de-semana",
      row_normal: "Normal 08h00 – 19h00",
      row_intermediate: "Intermédio 06h00 – 08h00 · 19h00 – 22h00",
      row_night: "Noturno 22h00 – 06h00",
      row_weekend: "Fim-de-semana (Sáb. & Dom.)",
      footer_note: "Preços em francos suíços (CHF). Pagamento a bordo também aceite em EUR / USD com conversão favorável ao CHF.",
      footer_see: "Consulte os",
      footer_terms: "Termos e Condições",
      footer_for: "para a política completa.",
    },
  },

  de: {
    terms: {
      eyebrow: "— Rechtliches",
      title: "Allgemeine Geschäftsbedingungen",
      sections: {
        s1_title: "1. Vorauszahlung",
        s1_body: "Alle Reservationen von SwissAlpenLine werden nach vollständiger Vorauszahlung bestätigt, ähnlich wie bei Flugbuchungen. Die Gelder werden von unserem Zahlungsdienstleister zurückgehalten und erst nach Abschluss der Fahrt an das Unternehmen freigegeben. Das schützt beide Seiten: Der Kunde erhält die garantierte Leistung, und das Unternehmen ist gegen unbegründete Rückbuchungen geschützt.",
        s2_title: "2. Servicezeiten & Zuschläge",
        table_tier: "Stufe",
        table_hours: "Zeitraum",
        table_surcharge: "Zuschlag",
        tier_normal: "Normal",
        tier_intermediate: "Zwischen",
        tier_night: "Nacht",
        s3_title: "3. Stornierungsbedingungen",
        table_notice: "Vorankündigung",
        table_outcome: "Regelung",
        notice_48: "≥ 48 h vorher",
        notice_24_48: "24 – 48 h vorher",
        notice_lt24: "< 24 h oder Nichterscheinen",
        outcome_full_refund: "Volle Rückerstattung",
        outcome_half: "50% Rückerstattung + 50% Guthaben (6 Mon.)",
        outcome_full_credit: "Volles Guthaben (6 Monate)",
        s3_note: "Keine Reservation verfällt — der bezahlte Betrag wird stets entweder erstattet oder in ein Guthaben umgewandelt, das sechs Monate lang für jede zukünftige Fahrt gültig ist.",
        s4_title: "4. Reservationsänderungen",
        s4_body: "Kostenlos bis 48 h vor Abholung, vorbehaltlich der Verfügbarkeit. Änderungen innerhalb von weniger als 48 h folgen der obigen Stornotabelle.",
        s5_title: "5. Voranmeldung ausserhalb der Normalzeiten",
        s5_body: "Zwischenzeit: mindestens 6 h Vorlauf. Nachtzeit: mindestens 12 h Vorlauf.",
        s6_title: "6. Verspätungen & Wartezeit",
        s6_body: "Flughäfen: 45 Minuten kostenlose Wartezeit nach der Landung. Andere Abholungen: 10 Minuten. Danach gilt der übliche Wartesatz.",
        s7_title: "7. Betrugsschutz",
        s7_list: [
          "Bei Reservationen über CHF 500 ist eine Identitätsprüfung erforderlich — der Name auf Karte/Twint muss mit dem Fahrgast übereinstimmen.",
          "SwissAlpenLine behält sich das Recht vor, den Service bei begründetem Betrugsverdacht zu verweigern, mit sofortiger voller Rückerstattung.",
          "Böswillige Rückbuchungen nach erbrachter Leistung können nach Art. 146 des Schweizerischen Strafgesetzbuchs Betrug darstellen und werden dokumentiert und den zuständigen Behörden gemeldet.",
          "Automatische Aufzeichnung des Leistungsnachweises (Abhol- und Absetzzeit im System bestätigt) zur Abwehr unbegründeter Rückbuchungen.",
        ],
        s8_title: "8. Rechtsgrundlage",
        s8_body: "Diese Regelung stützt sich auf die Vertragsfreiheit gemäss Art. 1 & 19–20 OR, die Reservationslogik begrenzter Kapazität (Art. 377 OR), die Transparenzvorgaben des Bundesgesetzes gegen den unlauteren Wettbewerb (Art. 8 UWG) sowie der Preisbekanntgabeverordnung (PBV), welche verlangt, dass der Endpreis — inkl. Zuschläge — vor der Zahlungsbestätigung angezeigt wird.",
      },
    },
    copyright: {
      eyebrow: "— Rechtliches",
      title: "Urheberrecht & geistiges Eigentum",
      lead: "© SwissAlpenLine. Alle Rechte vorbehalten.",
      body1: "Alle Inhalte dieser Website — Texte, Fotografien, visuelle Identität, Logo und Design — sind durch das Schweizerische Bundesgesetz über das Urheberrecht und verwandte Schutzrechte (URG, SR 231.1) geschützt. Die Vervielfältigung, Verbreitung oder Nutzung dieser Inhalte, ganz oder teilweise, ohne vorherige schriftliche Zustimmung von SwissAlpenLine ist untersagt.",
      body2: "Das Logo und die visuelle Identität von SwissAlpenLine sind ausschliessliches Eigentum des Unternehmens und nicht zur Verwendung durch Dritte lizenziert.",
      contact: "Für Lizenzanfragen kontaktieren Sie bitte info@swissalpenline.ch.",
    },
    tariff: {
      eyebrow: "— Tarif",
      title: "Tarife",
      subtitle: "Endpreise, keine versteckten Kosten — gemäss der Schweizerischen Preisbekanntgabeverordnung.",
      block_transfer: "Transfer — Punkt zu Punkt",
      block_fullday: "Chauffeur für einen Tag",
      block_courier: "Kurier — Dokumente",
      block_hours: "Stundenzuschläge",
      row_base: "Grundtarif",
      row_km_short: "Pro km (bis 80 km)",
      row_km_long: "Pro km (über 80 km)",
      row_child_seat: "Kindersitz",
      row_pet_no: "Tier, ohne Transportbox",
      row_pet_yes: "Tier + gestellte Transportbox",
      row_full_base: "Basis — 8 h",
      row_full_extra: "Zusätzliche Stunde",
      row_full_region: "Region",
      row_region_val: "CH · FR · DE · IT",
      row_courier_base: "Basis",
      row_courier_km: "Pro km",
      row_courier_night: "Nacht (22:00 – 06:00)",
      row_courier_weekend: "Wochenende",
      row_normal: "Normal 08:00 – 19:00",
      row_intermediate: "Zwischen 06:00 – 08:00 · 19:00 – 22:00",
      row_night: "Nacht 22:00 – 06:00",
      row_weekend: "Wochenende (Sa. & So.)",
      footer_note: "Preise in Schweizer Franken (CHF). Zahlung an Bord auch in EUR / USD, mit CHF-günstiger Umrechnung.",
      footer_see: "Siehe",
      footer_terms: "Allgemeine Geschäftsbedingungen",
      footer_for: "für die vollständige Regelung.",
    },
  },

  fr: {
    terms: {
      eyebrow: "— Mentions légales",
      title: "Conditions générales",
      sections: {
        s1_title: "1. Paiement anticipé",
        s1_body: "Toutes les réservations SwissAlpenLine sont confirmées par un paiement intégral anticipé, comme pour les compagnies aériennes. Les fonds sont retenus par notre prestataire de paiement et libérés au profit de la société uniquement après la fin du trajet. Cette architecture protège les deux parties : le client a la garantie du service, et l'entreprise est protégée contre les rétrofacturations abusives.",
        s2_title: "2. Horaires & suppléments",
        table_tier: "Plage",
        table_hours: "Horaire",
        table_surcharge: "Supplément",
        tier_normal: "Normal",
        tier_intermediate: "Intermédiaire",
        tier_night: "Nocturne",
        s3_title: "3. Politique d'annulation",
        table_notice: "Préavis",
        table_outcome: "Traitement",
        notice_48: "≥ 48 h avant",
        notice_24_48: "24 – 48 h avant",
        notice_lt24: "< 24 h ou non-présentation",
        outcome_full_refund: "Remboursement intégral",
        outcome_half: "50% remboursés + 50% en avoir (6 mois)",
        outcome_full_credit: "Avoir intégral (6 mois)",
        s3_note: "Aucune réservation n'est jamais perdue — le montant payé est toujours soit remboursé, soit converti en avoir valable six mois sur tout trajet futur.",
        s4_title: "4. Modifications de réservation",
        s4_body: "Sans frais jusqu'à 48 h avant la prise en charge, sous réserve de disponibilité. Toute modification à moins de 48 h suit la grille d'annulation ci-dessus.",
        s5_title: "5. Préavis hors horaire normal",
        s5_body: "Horaire intermédiaire : préavis minimum de 6 h. Horaire nocturne : préavis minimum de 12 h.",
        s6_title: "6. Retards & attente",
        s6_body: "Aéroports : 45 minutes d'attente gratuite après l'atterrissage. Autres prises en charge : 10 minutes. Au-delà, le tarif d'attente en vigueur s'applique.",
        s7_title: "7. Protection contre la fraude",
        s7_list: [
          "Vérification d'identité requise pour toute réservation supérieure à CHF 500 — le nom sur la carte / Twint doit correspondre à celui du passager.",
          "SwissAlpenLine se réserve le droit de refuser le service en cas de soupçon fondé de fraude, avec remboursement intégral immédiat.",
          "Les rétrofacturations abusives après un service rendu peuvent constituer une fraude au sens de l'art. 146 du Code pénal suisse, et seront documentées et signalées aux autorités compétentes.",
          "Enregistrement automatique de la preuve du service (horodatage de prise en charge et de dépose) pour la défense en cas de rétrofacturation abusive.",
        ],
        s8_title: "8. Base légale",
        s8_body: "La présente politique s'appuie sur la liberté contractuelle (art. 1 & 19–20 CO), la logique de réservation à capacité limitée (art. 377 CO), les exigences de transparence de la Loi contre la concurrence déloyale (art. 8 LCD) et l'Ordonnance sur l'indication des prix (OIP), qui impose que le prix final — suppléments inclus — soit affiché avant la confirmation du paiement.",
      },
    },
    copyright: {
      eyebrow: "— Mentions légales",
      title: "Droits d'auteur & propriété intellectuelle",
      lead: "© SwissAlpenLine. Tous droits réservés.",
      body1: "L'ensemble du contenu de ce site — textes, photographies, identité visuelle, logo et design — est protégé par la Loi fédérale suisse sur le droit d'auteur et les droits voisins (LDA, RS 231.1). La reproduction, la distribution ou l'utilisation totale ou partielle de ce contenu sans l'autorisation écrite préalable de SwissAlpenLine est interdite.",
      body2: "Le logo et l'identité visuelle de SwissAlpenLine sont la propriété exclusive de l'entreprise et ne sont pas concédés en licence à des tiers.",
      contact: "Pour toute demande de licence, veuillez contacter info@swissalpenline.ch.",
    },
    tariff: {
      eyebrow: "— Tarifs",
      title: "Tarifs",
      subtitle: "Prix finaux, sans coûts cachés — conformément à l'Ordonnance suisse sur l'indication des prix.",
      block_transfer: "Transfert — point à point",
      block_fullday: "Chauffeur à la journée",
      block_courier: "Courrier — Documents",
      block_hours: "Suppléments horaires",
      row_base: "Prise en charge",
      row_km_short: "Par km (jusqu'à 80 km)",
      row_km_long: "Par km (au-delà de 80 km)",
      row_child_seat: "Siège enfant",
      row_pet_no: "Animal, sans caisse",
      row_pet_yes: "Animal + caisse fournie",
      row_full_base: "Base — 8 h",
      row_full_extra: "Heure supplémentaire",
      row_full_region: "Région",
      row_region_val: "CH · FR · DE · IT",
      row_courier_base: "Base",
      row_courier_km: "Par km",
      row_courier_night: "Nocturne (22h00 – 06h00)",
      row_courier_weekend: "Week-end",
      row_normal: "Normal 08h00 – 19h00",
      row_intermediate: "Intermédiaire 06h00 – 08h00 · 19h00 – 22h00",
      row_night: "Nocturne 22h00 – 06h00",
      row_weekend: "Week-end (sam. & dim.)",
      footer_note: "Prix en francs suisses (CHF). Paiement à bord aussi en EUR / USD avec conversion favorable au CHF.",
      footer_see: "Voir les",
      footer_terms: "Conditions générales",
      footer_for: "pour la politique complète.",
    },
  },

  it: {
    terms: {
      eyebrow: "— Legale",
      title: "Termini e Condizioni",
      sections: {
        s1_title: "1. Pagamento anticipato",
        s1_body: "Tutte le prenotazioni SwissAlpenLine sono confermate al ricevimento del pagamento integrale anticipato, come per le compagnie aeree. I fondi sono trattenuti dal nostro fornitore di pagamento e rilasciati all'azienda solo dopo la conclusione del viaggio. Questa architettura tutela entrambe le parti: al cliente è garantito il servizio, l'azienda è protetta da contestazioni infondate.",
        s2_title: "2. Orari e supplementi",
        table_tier: "Fascia",
        table_hours: "Orario",
        table_surcharge: "Supplemento",
        tier_normal: "Normale",
        tier_intermediate: "Intermedio",
        tier_night: "Notturno",
        s3_title: "3. Politica di cancellazione",
        table_notice: "Preavviso",
        table_outcome: "Trattamento",
        notice_48: "≥ 48 h prima",
        notice_24_48: "24 – 48 h prima",
        notice_lt24: "< 24 h o mancata presentazione",
        outcome_full_refund: "Rimborso totale",
        outcome_half: "50% rimborso + 50% credito (6 mesi)",
        outcome_full_credit: "Credito totale (6 mesi)",
        s3_note: "Nessuna prenotazione va perduta — l'importo pagato viene sempre rimborsato o convertito in un credito valido sei mesi su qualsiasi viaggio futuro.",
        s4_title: "4. Modifiche alla prenotazione",
        s4_body: "Gratuite fino a 48 h prima del ritiro, salvo disponibilità. Modifiche a meno di 48 h seguono la tabella di cancellazione sopra riportata.",
        s5_title: "5. Preavviso fuori orario normale",
        s5_body: "Fascia intermedia: preavviso minimo 6 h. Fascia notturna: preavviso minimo 12 h.",
        s6_title: "6. Ritardi e tempo di attesa",
        s6_body: "Aeroporti: 45 minuti di attesa gratuita dopo l'atterraggio. Altri ritiri: 10 minuti. Oltre tale limite si applica la tariffa di attesa in vigore.",
        s7_title: "7. Protezione contro le frodi",
        s7_list: [
          "Verifica d'identità obbligatoria per prenotazioni superiori a CHF 500 — il nome su carta / Twint deve corrispondere al passeggero.",
          "SwissAlpenLine si riserva il diritto di rifiutare il servizio in caso di ragionevole sospetto di frode, con rimborso integrale immediato.",
          "Le contestazioni di pagamento in mala fede dopo la prestazione del servizio possono costituire frode ai sensi dell'art. 146 del Codice penale svizzero e saranno documentate e segnalate alle autorità competenti.",
          "Registrazione automatica della prova di servizio (orari di ritiro e riconsegna confermati nel sistema) per contestare eventuali chargeback indebiti.",
        ],
        s8_title: "8. Base legale",
        s8_body: "La presente politica si fonda sulla libertà contrattuale prevista dagli artt. 1 e 19–20 del Codice delle obbligazioni (CO) svizzero, sulla logica di prenotazione a capacità limitata (art. 377 CO), sui requisiti di trasparenza della Legge federale contro la concorrenza sleale (art. 8 LCSl) e dell'Ordinanza sull'indicazione dei prezzi (OIP), che impone di mostrare il prezzo finale — supplementi inclusi — prima della conferma del pagamento.",
      },
    },
    copyright: {
      eyebrow: "— Legale",
      title: "Diritti d'autore e proprietà intellettuale",
      lead: "© SwissAlpenLine. Tutti i diritti riservati.",
      body1: "Tutti i contenuti di questo sito — testi, fotografie, identità visiva, logo e design — sono protetti dalla Legge federale svizzera sul diritto d'autore e sui diritti di protezione affini (LDA, RS 231.1). Sono vietate la riproduzione, distribuzione o utilizzazione, totali o parziali, di tali contenuti senza autorizzazione scritta preventiva di SwissAlpenLine.",
      body2: "Il logo e l'identità visiva di SwissAlpenLine sono di proprietà esclusiva dell'azienda e non sono concessi in licenza a terzi.",
      contact: "Per richieste di licenza contattare info@swissalpenline.ch.",
    },
    tariff: {
      eyebrow: "— Tariffe",
      title: "Tariffe",
      subtitle: "Prezzi finali, senza costi nascosti — come richiesto dall'Ordinanza svizzera sull'indicazione dei prezzi.",
      block_transfer: "Transfer — punto a punto",
      block_fullday: "Chauffeur per un giorno",
      block_courier: "Corriere — Documenti",
      block_hours: "Supplementi orari",
      row_base: "Tariffa base",
      row_km_short: "Per km (fino a 80 km)",
      row_km_long: "Per km (oltre 80 km)",
      row_child_seat: "Seggiolino bambino",
      row_pet_no: "Animale, senza trasportino",
      row_pet_yes: "Animale + trasportino fornito",
      row_full_base: "Base — 8 h",
      row_full_extra: "Ora extra",
      row_full_region: "Regione",
      row_region_val: "CH · FR · DE · IT",
      row_courier_base: "Base",
      row_courier_km: "Per km",
      row_courier_night: "Notturno (22:00 – 06:00)",
      row_courier_weekend: "Weekend",
      row_normal: "Normale 08:00 – 19:00",
      row_intermediate: "Intermedio 06:00 – 08:00 · 19:00 – 22:00",
      row_night: "Notturno 22:00 – 06:00",
      row_weekend: "Weekend (sab. & dom.)",
      footer_note: "Prezzi in franchi svizzeri (CHF). Pagamento a bordo accettato anche in EUR / USD con conversione favorevole al CHF.",
      footer_see: "Consulta i",
      footer_terms: "Termini e Condizioni",
      footer_for: "per la politica completa.",
    },
  },
};

export const getLegal = (lang) => LEGAL[lang] || LEGAL.en;
